import moment from "moment";
import { useMemo } from "react";
import { typeOptions } from "../../constants";
import { Contractor } from "../../entities/contractor";
import { Gig } from "../../entities/gig";
import { Project } from "../../entities/project";
import { Stage } from "../../entities/stage";
import { Venue } from "../../entities/venue";
import { compatibleSources } from "../../global";
import { useContractors } from "../../redux/contractor/contractorHooks";
import { useGigs } from "../../redux/gig/gigHooks";
import { useProjects } from "../../redux/project/projectHooks";
import { useStages } from "../../redux/stage/stageHooks";
import { useVenues } from "../../redux/venue/venueHooks";

/** Date range as inclusive `YYYY-MM-DD` strings. An empty string means "open". */
export type DateRange = { from: string; to: string };

/**
 * A service with its related records resolved — shape shared with the
 * services list / export dialog (`ResolvedGig`).
 */
export type ResolvedService = {
  gig: Gig;
  venue?: Venue;
  contractor?: Contractor;
  project?: Project;
  stage?: Stage;
};

/**
 * High-contrast palette assigned to organizations by rank, so the top
 * organizations get distinct, recognizable colors that stay consistent across
 * every chart on the page (leaderboard bars and stacked activity segments).
 */
export const ORG_PALETTE = [
  "#FF3B30", // red
  "#0A84FF", // blue
  "#FF9500", // orange
  "#34C759", // green
  "#AF52DE", // purple
  "#5AC8FA", // teal
  "#FF2D55", // pink
  "#5856D6", // indigo
];

/** Color for organizations beyond the palette and services with no org. */
export const OTHER_ORG_COLOR = "#C7C7CC";

export type OrgStat = {
  id: number;
  name: string;
  count: number;
  hours: number;
  color: string;
};

/** A single organization's slice of one month, for the stacked activity bar. */
export type MonthSegment = {
  id: number; // organization id, or 0 for the "Other" bucket
  name: string;
  count: number;
  hours: number;
  color: string;
};

export type MonthBucket = {
  key: string; // YYYY-MM
  label: string; // e.g. "Jan"
  year: number;
  count: number;
  hours: number;
  segments: MonthSegment[];
};

export type TypeStat = {
  sessionTypeID: number;
  title: string;
  icon: string;
  count: number;
  hours: number;
};

export type VenueStat = {
  id: number;
  name: string;
  location: string; // "City, ST" when available
  count: number;
  hours: number;
};

export type Insights = {
  isLoading: boolean;
  /** Whether the musician has any performed service at all (ignores the range). */
  hasAnyData: boolean;
  servicesCount: number;
  totalHours: number;
  organizationsCount: number;
  venuesCount: number;
  avgPerMonth: number;
  topOrg?: OrgStat;
  busiestMonth?: MonthBucket;
  organizations: OrgStat[];
  venues: VenueStat[];
  months: MonthBucket[];
  types: TypeStat[];
  /** Performed services within the range, resolved — for drill-down dialogs. */
  services: ResolvedService[];
  /** Every service, resolved and unfiltered — for the export dialog. */
  allServices: ResolvedService[];
};

/**
 * A service counts as "performed" — the same definition the work-history
 * export uses: the musician is actually playing, the job is not sitting in a
 * terminus stage (declined, cancelled…), and it has a real date.
 *
 * @param gig the service
 * @param terminus whether its current stage is a terminus stage
 * @returns {boolean} true if the service should be counted
 */
function isPerformed(gig: Gig, terminus?: boolean): boolean {
  return gig.isPlaying() && !terminus && !gig.toBeDetermined();
}

/**
 * Duration of a service in hours, anchored to the venue timezone. Services
 * with no time set (midnight → midnight) or inverted dates contribute 0.
 *
 * @param gig the service
 * @param tz IANA timezone name of the venue, if known
 * @returns {number} non-negative hours
 */
function durationHours(gig: Gig, tz?: string): number {
  const minutes = gig.to(tz).diff(gig.from(tz), "minutes");
  if (!isFinite(minutes) || minutes <= 0) return 0;
  return minutes / 60;
}

/**
 * Builds a continuous month axis for the activity chart. Uses the selected
 * range bounds when present, otherwise the extent of the data. Capped to the
 * most recent 36 months so the chart stays legible — the headline totals are
 * always computed over the full filtered set, never this axis.
 *
 * @param range the selected date range
 * @param monthMap per-month aggregates keyed by `YYYY-MM`
 * @returns {MonthBucket[]} ordered buckets, oldest first
 */
function buildMonthAxis(
  range: DateRange,
  monthMap: Map<string, { count: number; hours: number }>
): MonthBucket[] {
  const keys = [...monthMap.keys()].sort();
  let start = range.from
    ? moment(range.from, "YYYY-MM-DD").startOf("month")
    : keys.length
    ? moment(keys[0], "YYYY-MM")
    : moment().startOf("month");
  let end = range.to
    ? moment(range.to, "YYYY-MM-DD").startOf("month")
    : keys.length
    ? moment(keys[keys.length - 1], "YYYY-MM")
    : moment().startOf("month");

  if (end.isBefore(start)) end = start.clone();

  const MAX_BARS = 36;
  if (end.diff(start, "months") > MAX_BARS - 1) {
    start = end.clone().subtract(MAX_BARS - 1, "months");
  }

  const buckets: MonthBucket[] = [];
  const cursor = start.clone();
  while (!cursor.isAfter(end, "month")) {
    const key = cursor.format("YYYY-MM");
    const agg = monthMap.get(key);
    buckets.push({
      key,
      label: cursor.format("MMM"),
      year: cursor.year(),
      count: agg?.count ?? 0,
      hours: agg?.hours ?? 0,
      segments: [],
    });
    cursor.add(1, "month");
  }
  return buckets;
}

/**
 * Computes a musician's performance insights over a date range from their
 * services, resolving each against its venue (timezone), organization and
 * stage exactly as the services list does.
 *
 * @param range the date range to analyze
 * @returns {Insights} the computed metrics
 */
export function useInsights(range: DateRange): Insights {
  const { gigs, isLoading: gigsLoading } = useGigs();
  const { contractors, isLoading: contractorsLoading } = useContractors();
  const { venues, isLoading: venuesLoading } = useVenues();
  const { stages, isLoading: stagesLoading } = useStages();
  const { projects } = useProjects();

  const isLoading =
    gigsLoading || contractorsLoading || venuesLoading || stagesLoading;

  return useMemo<Insights>(() => {
    const allGigs = gigs ?? [];

    const resolved: ResolvedService[] = allGigs.map((gig) => ({
      gig,
      venue: venues?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.venueID
      ),
      contractor: contractors?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.contractorID
      ),
      project: projects?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.projectID
      ),
      stage: stages?.find((s) => s.id === gig.stageID),
    }));

    const performedAll = resolved.filter(({ gig, stage }) =>
      isPerformed(gig, stage?.terminus)
    );

    const fromM = range.from
      ? moment(range.from, "YYYY-MM-DD").startOf("day")
      : null;
    const toM = range.to ? moment(range.to, "YYYY-MM-DD").endOf("day") : null;

    const inRange = performedAll.filter(({ gig, venue }) => {
      const m = gig.from(venue?.tzName);
      if (fromM && m.isBefore(fromM)) return false;
      if (toM && m.isAfter(toM)) return false;
      return true;
    });

    let totalHours = 0;
    const orgMap = new Map<number, OrgStat>();
    const venueMap = new Map<number, VenueStat>();
    const typeMap = new Map<number, { count: number; hours: number }>();
    const monthMap = new Map<string, { count: number; hours: number }>();
    // month key → (organization id, or 0 for "no organization") → aggregate
    const monthOrgMap = new Map<
      string,
      Map<number, { count: number; hours: number }>
    >();

    inRange.forEach(({ gig, venue, contractor }) => {
      const tz = venue?.tzName;
      const hours = durationHours(gig, tz);
      totalHours += hours;

      if (contractor) {
        const cur =
          orgMap.get(contractor.id) ??
          ({
            id: contractor.id,
            name: contractor.name || "Unknown organization",
            count: 0,
            hours: 0,
            color: OTHER_ORG_COLOR,
          } as OrgStat);
        cur.count += 1;
        cur.hours += hours;
        orgMap.set(contractor.id, cur);
      }

      if (venue) {
        const loc = [venue.city, venue.state].filter(Boolean).join(", ");
        const cur =
          venueMap.get(venue.id) ??
          ({
            id: venue.id,
            name: venue.name || "Unknown venue",
            location: loc,
            count: 0,
            hours: 0,
          } as VenueStat);
        cur.count += 1;
        cur.hours += hours;
        venueMap.set(venue.id, cur);
      }

      const t = typeMap.get(gig.sessionTypeID) ?? { count: 0, hours: 0 };
      t.count += 1;
      t.hours += hours;
      typeMap.set(gig.sessionTypeID, t);

      const mk = gig.from(tz).format("YYYY-MM");
      const mb = monthMap.get(mk) ?? { count: 0, hours: 0 };
      mb.count += 1;
      mb.hours += hours;
      monthMap.set(mk, mb);

      const orgKey = contractor?.id ?? 0;
      let inner = monthOrgMap.get(mk);
      if (!inner) {
        inner = new Map();
        monthOrgMap.set(mk, inner);
      }
      const seg = inner.get(orgKey) ?? { count: 0, hours: 0 };
      seg.count += 1;
      seg.hours += hours;
      inner.set(orgKey, seg);
    });

    // Rank organizations, then assign each a stable palette color by rank so
    // the same organization is the same color in every chart on the page.
    const ranked = [...orgMap.values()].sort(
      (a, b) => b.count - a.count || b.hours - a.hours || a.id - b.id
    );
    const colorById = new Map<number, string>();
    const rankById = new Map<number, number>();
    ranked.forEach((o, i) => {
      const color = i < ORG_PALETTE.length ? ORG_PALETTE[i] : OTHER_ORG_COLOR;
      o.color = color;
      colorById.set(o.id, color);
      rankById.set(o.id, i);
    });
    const organizations = ranked;

    const venueStats = [...venueMap.values()].sort(
      (a, b) => b.count - a.count || b.hours - a.hours || a.id - b.id
    );

    const types: TypeStat[] = typeOptions
      .map((t) => {
        const agg = typeMap.get(t.sessionTypeID);
        return {
          sessionTypeID: t.sessionTypeID,
          title: t.title,
          icon: t.icon,
          count: agg?.count ?? 0,
          hours: agg?.hours ?? 0,
        };
      })
      .filter((t) => t.count > 0)
      .sort((a, b) => b.count - a.count);

    const months = buildMonthAxis(range, monthMap);
    // Split each month into organization segments (palette-colored orgs, with
    // overflow orgs and no-org services folded into a single "Other" slice),
    // ordered by global rank so the stacking order is stable month to month.
    months.forEach((m) => {
      const inner = monthOrgMap.get(m.key);
      if (!inner) return;
      const colored: MonthSegment[] = [];
      let otherCount = 0;
      let otherHours = 0;
      inner.forEach((agg, orgId) => {
        const color = colorById.get(orgId);
        if (orgId > 0 && color && color !== OTHER_ORG_COLOR) {
          colored.push({
            id: orgId,
            name: orgMap.get(orgId)?.name ?? "Organization",
            count: agg.count,
            hours: agg.hours,
            color,
          });
        } else {
          otherCount += agg.count;
          otherHours += agg.hours;
        }
      });
      colored.sort(
        (a, b) => (rankById.get(a.id) ?? 0) - (rankById.get(b.id) ?? 0)
      );
      if (otherCount > 0) {
        colored.push({
          id: 0,
          name: "Other",
          count: otherCount,
          hours: otherHours,
          color: OTHER_ORG_COLOR,
        });
      }
      m.segments = colored;
    });

    // Derived from the full in-range data (not the capped chart axis) so an
    // all-time range spanning many years still reports the true peak month.
    let busiestMonth: MonthBucket | undefined;
    monthMap.forEach((agg, key) => {
      if (!busiestMonth || agg.count > busiestMonth.count) {
        const m = moment(key, "YYYY-MM");
        busiestMonth = {
          key,
          label: m.format("MMM"),
          year: m.year(),
          count: agg.count,
          hours: agg.hours,
          segments: [],
        };
      }
    });
    const avgPerMonth = months.length ? inRange.length / months.length : 0;

    return {
      isLoading,
      hasAnyData: performedAll.length > 0,
      servicesCount: inRange.length,
      totalHours,
      organizationsCount: orgMap.size,
      venuesCount: venueMap.size,
      avgPerMonth,
      topOrg: organizations[0],
      busiestMonth,
      organizations,
      venues: venueStats,
      months,
      types,
      services: inRange,
      allServices: resolved,
    };
  }, [
    gigs,
    contractors,
    venues,
    stages,
    projects,
    isLoading,
    range.from,
    range.to,
  ]);
}
